<template>
  <f7-page>
    <div v-if="!loaded" class="preloader-overlay">
      <div class="preloader-card">
        <f7-preloader></f7-preloader>
        <div class="preloader-text">
          {{ $t('loading') }}
        </div>
      </div>
    </div>

    <div v-if="loaded" class="custom-table-container tableContainer">
      <MiningSamplesTable :store="customStore" />
    </div>
  </f7-page>
</template>

<script>
import MiningSamplesTable from '@/components/Mining/SamplesTable/index.vue';
import { mapActions } from 'vuex';
import EventBus from '@/services/event-bus';
import { NOTIFY_TIME_LONG, ATCS_ES } from '@/js/constants';

export default {
  name: 'MiningSamplesPage',
  components: {
    MiningSamplesTable,
  },
  data() {
    return {
      loaded: false,
      customStore: null,
      miningSamplesHandler: null,
    };
  },
  beforeMount() {
    this.setCurrentRoute([
      {
        name: this.$t('Mining'),
        disabled: false,
        page: 'MiningSamplesPage',
        active: false,
      },
      {
        name: this.$t('agronomySample.exportExcelSampleTable'),
        disabled: true,
        page: 'MiningSamplesPage',
        active: true,
      },
    ]);

    this.setCurrentFilter('mining');

    // ✅ Configurar filtros si viene desde dashboard
    const flagAlert = this.$f7route?.query?.dashboardAlert;
    const flagInProcess = this.$f7route?.query?.dashboardInProcess;
    const flagNotReviewed =
      this.$f7route?.query?.dashboardNotReviewed;

    if (
      flagAlert === 'true' ||
      flagInProcess === 'true' ||
      flagNotReviewed === 'true'
    ) {
      const params = {
        receptionDate_after: '',
        receptionDate_before: '',
        endDate_after: '',
        endDate_before: '',
        sample_type: '',
        analyse_type: '',
        atc_name: ATCS_ES.mining,
        summary_semaphore: '',
        status: '',
        not_seen: '',
      };

      if (flagAlert === 'true') {
        params.summary_semaphore = 'red,orange';
      }

      if (flagInProcess === 'true') {
        params.status = '2';
      }

      if (flagNotReviewed === 'true') {
        params.not_seen = true;
      }
      const endDate = new Date();
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 6);

      const formatDate = (date) => {
        const d = new Date(date);
        let month = '' + (d.getMonth() + 1);
        let day = '' + d.getDate();
        const year = d.getFullYear();
        if (month.length < 2) month = '0' + month;
        if (day.length < 2) day = '0' + day;
        return [year, month, day].join('-');
      };

      params.receptionDate_after = formatDate(startDate);
      params.receptionDate_before = formatDate(endDate);

      this.setAllFilterParams(params);
    }
  },
  async mounted() {
    try {
      this.loaded = false;

      const isAlert = this.$f7route?.query?.dashboardAlert === 'true';
      const isInProcess =
        this.$f7route?.query?.dashboardInProcess === 'true';
      const isNotReviewed =
        this.$f7route?.query?.dashboardNotReviewed === 'true';

      const fromDashboard = isAlert || isInProcess || isNotReviewed;
      let hasQuickFilters = false;
      const raw = localStorage.getItem('miningSamplesTable');
      if (raw) {
        try {
          const saved = JSON.parse(raw);
          const f = saved.isFilterActive || {};
          hasQuickFilters =
            f.inProcess ||
            f.finished ||
            f.cancelled ||
            f.lastMonth ||
            f.noReviewed ||
            f.onAlert ||
            f.noPositives;
        } catch (e) {
          console.warn(
            'miningSamplesTable corrupto en localStorage',
            e
          );
        }
      }

      if (fromDashboard) {
        const params = {
          receptionDate_after: '',
          receptionDate_before: '',
          endDate_after: '',
          endDate_before: '',
          sample_type: '',
          analyse_type: '',
          atc_name: ATCS_ES.mining,
          summary_semaphore: '',
          status: '',
          not_seen: '',
        };

        if (isAlert) params.summary_semaphore = 'red,orange';
        if (isInProcess) params.status = '2';
        if (isNotReviewed) params.not_seen = true;

        const { receptionDate_after, receptionDate_before } =
          this.getLastMonthsDates(6);
        params.receptionDate_after = receptionDate_after;
        params.receptionDate_before = receptionDate_before;

        this.setAllFilterParams(params);
        this.customStore = await this.getStoreWithFilters(params);
      } else if (!hasQuickFilters) {
        // ✅ Solo si NO hay filtros rápidos guardados usamos los filtros globales
        setTimeout(() => {
          this.applyCurrentFilters();
        }, 0);
      }
      // Si hasQuickFilters === true y no vienes del dashboard:
      // no hacemos nada aquí; el hijo aplicará sus filtros en beforeMount.

      this.miningSamplesHandler = async (params) => {
        if (params)
          this.customStore = await this.getStoreWithFilters(params);
        else this.customStore = await this.getStore();
      };

      EventBus.$on('MiningSamplesPage', this.miningSamplesHandler);
    } catch (error) {
      this.$notifyDX(
        {
          message: this.$t(
            this.$helpers.getFilteredErrorMessage(
              JSON.parse(error.message).error
            )
          ),
          width: 550,
        },
        'error',
        NOTIFY_TIME_LONG
      );
    } finally {
      this.loaded = true;
    }
  },
  beforeDestroy() {
    if (this.miningSamplesHandler) {
      EventBus.$off('MiningSamplesPage', this.miningSamplesHandler);
    }
  },
  methods: {
    getLastMonthsDates(months) {
      const endDate = new Date();
      const startDate = new Date(endDate);

      const originalDay = startDate.getDate();
      const targetMonth = startDate.getMonth() - months;

      startDate.setMonth(targetMonth);

      if (startDate.getDate() !== originalDay) {
        startDate.setDate(0);
      }

      const formatDate = (date) => {
        const d = new Date(date);
        let month = '' + (d.getMonth() + 1);
        let day = '' + d.getDate();
        const year = d.getFullYear();
        if (month.length < 2) month = '0' + month;
        if (day.length < 2) day = '0' + day;
        return [year, month, day].join('-');
      };

      return {
        receptionDate_after: formatDate(startDate),
        receptionDate_before: formatDate(endDate),
      };
    },

    ...mapActions('miningSamples', [
      'getStore',
      'getStoreWithFilters',
    ]),
    ...mapActions('breadcrumb', ['setCurrentRoute']),
    ...mapActions('filter', [
      'setCurrentFilter',
      'applyCurrentFilters',
    ]),
    ...mapActions('samples', ['setAllFilterParams']),
  },
};
</script>
